import React from 'react';
import { Field } from 'react-final-form';
import { Column, Input, Checkbox } from '../design-system';
import { Block } from '../design-system';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import Select from './Select';
import CheckboxGroup from './CheckboxGroup';
import RadioGroup from './RadioGroup';
import { fieldFormat, sortByLabel } from './../helpers/utils';

interface SelectOption {
  label: string;
  value: any;
}

interface FieldColumnProps {
  id?: string;
  name: string;
  label?: string;
  component?: string;
  validate?: any;
  width?: any;
  fileOnChange?: (file: File) => void;
  onChange?: (value: any) => void;
  isClearable?: boolean;
  options?: SelectOption[];
  sortOptions?: boolean;
  [key: string]: any;
}

interface ErrorFieldProps {
  name: string;
}

const Error = styled.span`
  color: ${(p) => p.theme.colors.danger};
  font-size: 14px;
  margin-top: 4px;
  position: absolute;
  top: 100%;
  left: 8px;
`;

const ErrorField = ({ name }: ErrorFieldProps) => (
  <Field
    name={name}
    subscription={{ touched: true, error: true }}
    render={({ meta: { touched, error } }) =>
      touched && error ? <Error>{error}</Error> : null
    }
  />
);

const FieldColumn = ({
  id,
  name,
  label,
  component,
  validate,
  width,
  fileOnChange,
  onChange,
  isClearable = true,
  options = [],
  sortOptions = true,
  ...opts
}: FieldColumnProps) => {
  const navigate = useNavigate();
  const sortedOptions =
    sortOptions && options ? [...options].sort(sortByLabel) : options;

  return (
    <Column pb={3} width={width}>
      <Field parse={(value) => value} name={name} validate={validate}>
        {({ input }) => {
          const sharedProps = {
            id: fieldFormat(name, '-edit-field'),
            ...input,
            label,
            onChange: (value: any, potentialValue?: any) => {
              if (value === 'addnew') {
                navigate('/contacts');
              }

              input.onChange(potentialValue || value);

              if (onChange) {
                onChange(value);
              }
            },
            ...opts,
          };

          const inputProps = {
            ...sharedProps,
            onChange: (_: any, value: any, event: any) => {
              if (name === 'file') {
                fileOnChange && fileOnChange(event.target.files[0]);
              }
              input.onChange(value);
            },
          };

          const selectProps = {
            inputId: name,
            classNamePrefix: 'dropdown-menu',
            isClearable,
            placeholder: opts.placeholder || `Select a ${label}`,
            options: sortedOptions,
            ...sharedProps,
          };

          let Component: any = Input;
          let props: any = inputProps;

          switch (component) {
            case 'select':
              Component = Select;
              props = selectProps;
              break;
            case 'checkbox':
              Component = Checkbox;
              break;
            case 'checkbox-group':
              Component = CheckboxGroup;
              props = sharedProps;
              break;
            case 'radio':
              Component = RadioGroup;
              props = sharedProps;
              break;
            default:
              break;
          }

          return (
            <Block position="relative" mb="16px">
              <Component {...props} />
              <ErrorField name={name} />
            </Block>
          );
        }}
      </Field>
    </Column>
  );
};

export default FieldColumn;
